import { useParams, Link, useNavigate } from 'react-router-dom'
import { useApi, asArray } from '../../hooks/useApi'
import { ScoreGauge } from '../../components/ScoreGauge'
import { DonutChart } from '../../components/charts/DonutChart'
import { SparklineChart } from '../../components/charts/SparklineChart'
import { HealthRatioBar } from '../../components/ui/HealthRatioBar'
import { TopIssuesList } from '../../components/ui/TopIssuesList'
import { ContentHeader } from '../../components/layout/ContentHeader'
import { EmptyState } from '../../components/ui/EmptyState'
import { FileSearch, Play, Loader, CheckCircle, X, Zap } from 'lucide-react'
import { apiPost } from '../../lib/auth'
import { useState, useEffect, useRef } from 'react'

interface AuditData {
  id: number
  status: string
  score: number | null
  scoreTech: number | null
  scoreOnpage: number | null
  scorePerf: number | null
  scoreSerp: number | null
  pagesCount: number
  createdAt: string
}

interface Summary {
  total: number
  status200: number
  status301: number
  status302: number
  status404: number
  status5xx: number
  criticalCount: number
  warningCount: number
  infoCount: number
  topIssues: Array<{ type: string; title: string; severity: string; count: number }>
  healthyPages: number
  unhealthyPages: number
  indexablePages: number
  nonIndexablePages: number
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

function scoreColor(score: number): string {
  if (score >= 91) return '#10B981'
  if (score >= 71) return '#84CC16'
  if (score >= 31) return '#FBBF24'
  return '#EF4444'
}

function scoreLabel(score: number): string {
  if (score >= 91) return 'Excellent'
  if (score >= 71) return 'Bon'
  if (score >= 31) return 'Moyen'
  return 'Faible'
}

export default function AuditOverview() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [launchLoading, setLaunchLoading] = useState(false)
  const [justLaunched, setJustLaunched] = useState(false)
  const [completedNotif, setCompletedNotif] = useState(false)
  const prevRunningRef = useRef(false)

  const { data: audits, refetch } = useApi<AuditData[]>(`/api/clients/${projectId}/audits`)
  const auditList = asArray(audits)
  const latestAudit = auditList.find(a => a.status === 'completed')
  const hasRunning = auditList.some(a => a.status === 'pending' || a.status === 'running')
  const runningAudit = auditList.find(a => a.status === 'pending' || a.status === 'running')

  // Clear optimistic state once list catches up
  useEffect(() => {
    if (justLaunched && hasRunning) setJustLaunched(false)
  }, [justLaunched, hasRunning])

  // Poll while audit is running
  useEffect(() => {
    if (!hasRunning && !justLaunched) return
    const interval = setInterval(() => refetch(), 3000)
    return () => clearInterval(interval)
  }, [hasRunning, justLaunched, refetch])

  // Detect completion
  useEffect(() => {
    if (prevRunningRef.current && !hasRunning && !justLaunched) {
      setCompletedNotif(true)
      refetch()
    }
    prevRunningRef.current = hasRunning
  }, [hasRunning, justLaunched])

  const { data: summary, refetch: refetchSummary } = useApi<Summary>(
    latestAudit ? `/api/audits/${latestAudit.id}/summary` : '/api/health'
  )

  // Refetch summary when audit completes
  useEffect(() => {
    if (completedNotif) refetchSummary()
  }, [completedNotif])

  const scoreHistory = auditList
    .filter(a => a.status === 'completed' && a.score !== null)
    .reverse()
    .slice(-10)
    .map(a => a.score!)

  const launchDisabled = launchLoading || hasRunning || justLaunched

  const handleLaunchAudit = async () => {
    setLaunchLoading(true)
    setCompletedNotif(false)
    await apiPost(`/api/clients/${projectId}/audits`, {})
    setJustLaunched(true)
    setLaunchLoading(false)
    refetch()
  }

  if (!latestAudit) {
    return (
      <>
        <ContentHeader breadcrumb={[{ label: 'Audit de site Web' }, { label: 'Apercu global' }]} title="Apercu global" />
        <EmptyState
          icon={FileSearch}
          title="Aucun audit disponible"
          description="Lancez un audit pour analyser votre site."
          action={{ label: 'Lancer un audit', onClick: handleLaunchAudit }}
        />
      </>
    )
  }

  const score = latestAudit.score ?? 0

  return (
    <>
      <ContentHeader
        breadcrumb={[{ label: 'Audit de site Web' }, { label: 'Apercu global' }]}
        title="Apercu global"
        subtitle={`Dernier audit : ${formatDate(latestAudit.createdAt)} - ${latestAudit.pagesCount} pages crawlees`}
        actions={
          <button
            onClick={handleLaunchAudit}
            disabled={launchDisabled}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {(launchLoading || justLaunched || hasRunning) ? <Loader className="w-4 h-4 animate-spin" strokeWidth={1.25} /> : <Play className="w-4 h-4" strokeWidth={1.25} />}
            {launchLoading ? 'Lancement...' : (justLaunched || hasRunning) ? 'Audit en cours...' : 'Relancer l\'audit'}
          </button>
        }
      />

      {/* Audit en cours */}
      {(hasRunning || justLaunched) && (
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-xl px-5 py-4 mb-6">
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
            <Zap size={16} className="text-indigo-500 animate-pulse" />
          </div>
          <div>
            <p className="text-sm font-medium text-indigo-800">
              {runningAudit?.status === 'running' ? 'Audit en cours...' : 'Audit en attente...'}
            </p>
            <p className="text-xs text-indigo-600 mt-0.5">
              Les r&eacute;sultats seront mis &agrave; jour automatiquement une fois l'audit termin&eacute;.
            </p>
          </div>
        </div>
      )}

      {/* Notification: audit terminé */}
      {completedNotif && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 mb-6">
          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" strokeWidth={1.5} />
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-800">Audit termin&eacute; !</p>
            <p className="text-xs text-emerald-600 mt-0.5">
              Les r&eacute;sultats ont &eacute;t&eacute; mis &agrave; jour. Consultez le{' '}
              <Link to={`/p/${projectId}/audit/errors`} className="underline hover:no-underline font-medium">
                rapport d'erreurs
              </Link>{' '}
              pour le d&eacute;tail.
            </p>
          </div>
          <button onClick={() => setCompletedNotif(false)} className="p-1 rounded-lg hover:bg-emerald-100 transition-colors">
            <X className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
          </button>
        </div>
      )}

      {/* Row 1 : Score + Problemes + Ratio */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        {/* Score de sante */}
        <div className="bg-white border border-[#E2E4ED] rounded-2xl p-6">
          <h3 className="text-sm font-semibold text-[#1B2559] mb-4">Score de sante</h3>
          <div className="flex flex-col items-center">
            <ScoreGauge score={score} label={scoreLabel(score)} size="lg" />
            <p className="text-xs text-[#8B8FA7] mt-3">{score}% des pages sont sans erreur</p>
          </div>
          {scoreHistory.length > 1 && (
            <div className="mt-4">
              <p className="text-[10px] text-[#8B8FA7] uppercase tracking-wider mb-1">Historique</p>
              <SparklineChart data={scoreHistory} color={scoreColor(score)} height={36} />
            </div>
          )}
        </div>

        {/* Problemes par priorite */}
        {summary && (
          <div className="bg-white border border-[#E2E4ED] rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-[#1B2559] mb-4">Problemes par priorite</h3>
            <p className="text-3xl font-bold font-mono text-[#1B2559] mb-4">
              {summary.criticalCount + summary.warningCount + summary.infoCount}
              <span className="text-sm font-normal text-[#8B8FA7] ml-2">Problemes</span>
            </p>
            <div className="space-y-3">
              <Link to={`/p/${projectId}/audit/errors?severity=critical`} className="flex items-center justify-between group hover:bg-[#FEF2F2] rounded-lg px-2 py-1.5 -mx-2 transition-colors">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="text-[#1B2559] group-hover:text-red-600">Erreurs</span>
                </span>
                <span className="font-mono font-bold text-red-500">{summary.criticalCount}</span>
              </Link>
              <Link to={`/p/${projectId}/audit/errors?severity=warning`} className="flex items-center justify-between group hover:bg-[#FFFBEB] rounded-lg px-2 py-1.5 -mx-2 transition-colors">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span className="text-[#1B2559] group-hover:text-amber-600">Avertissements</span>
                </span>
                <span className="font-mono font-bold text-amber-500">{summary.warningCount}</span>
              </Link>
              <Link to={`/p/${projectId}/audit/errors?severity=info`} className="flex items-center justify-between group hover:bg-[#EFF6FF] rounded-lg px-2 py-1.5 -mx-2 transition-colors">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span className="text-[#1B2559] group-hover:text-blue-600">Notifications</span>
                </span>
                <span className="font-mono font-bold text-blue-500">{summary.infoCount}</span>
              </Link>
            </div>
            <Link to={`/p/${projectId}/audit/errors`} className="block text-center text-xs text-orion-primary hover:underline mt-4">
              Voir tous les problemes
            </Link>
          </div>
        )}

        {/* Ratio de sante des pages */}
        {summary && (
          <div className="bg-white border border-[#E2E4ED] rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-[#1B2559] mb-4">Ratio de sante des pages</h3>
            <HealthRatioBar healthy={summary.healthyPages} unhealthy={summary.unhealthyPages} total={summary.total} />
            <Link to={`/p/${projectId}/audit/pages?hasIssues=true`} className="block text-center text-xs text-orion-primary hover:underline mt-4">
              Voir les problemes par page
            </Link>
          </div>
        )}
      </div>

      {/* Row 2 : Top issues */}
      {summary && asArray(summary.topIssues).length > 0 && (
        <div className="bg-white border border-[#E2E4ED] rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-[#1B2559]">Les plus gros problemes</h3>
            <Link to={`/p/${projectId}/audit/errors`} className="text-xs text-orion-primary hover:underline">
              Voir tous les problemes
            </Link>
          </div>
          <TopIssuesList issues={asArray(summary.topIssues)} onClickIssue={(type) => navigate(`/p/${projectId}/audit/issues/${type}?auditId=${latestAudit.id}`)} />
        </div>
      )}

      {/* Row 3 : Codes HTTP + Indexation */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          {/* Codes HTTP */}
          <div className="bg-white border border-[#E2E4ED] rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-[#1B2559] mb-4">Codes HTTP</h3>
            <DonutChart
              data={[
                { name: '200 OK', value: summary.status200, color: '#10B981' },
                { name: '301 Redirect', value: summary.status301, color: '#6366F1' },
                { name: '302 Temp', value: summary.status302, color: '#FBBF24' },
                { name: '404 Not Found', value: summary.status404, color: '#EF4444' },
                { name: '5xx Server', value: summary.status5xx, color: '#991B1B' },
              ].filter(d => d.value > 0)}
              centerValue={summary.total}
              centerLabel="Pages"
              height={220}
            />
            <div className="flex flex-wrap gap-3 mt-3 justify-center">
              {[
                { label: '200', value: summary.status200, color: '#10B981' },
                { label: '301', value: summary.status301, color: '#6366F1' },
                { label: '302', value: summary.status302, color: '#FBBF24' },
                { label: '404', value: summary.status404, color: '#EF4444' },
                { label: '5xx', value: summary.status5xx, color: '#991B1B' },
              ].filter(d => d.value > 0).map(d => (
                <span key={d.label} className="flex items-center gap-1.5 text-xs text-[#8B8FA7]">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                  {d.label}: <span className="font-mono font-medium text-[#1B2559]">{d.value}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Indexation */}
          <div className="bg-white border border-[#E2E4ED] rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-[#1B2559] mb-4">Indexation des pages</h3>
            <div className="flex items-center gap-6 mb-6">
              <div>
                <p className="text-3xl font-bold font-mono text-[#1B2559]">{summary.indexablePages}</p>
                <p className="text-xs text-[#8B8FA7]">Indexables</p>
              </div>
              <div>
                <p className="text-3xl font-bold font-mono text-[#8B8FA7]">{summary.nonIndexablePages}</p>
                <p className="text-xs text-[#8B8FA7]">Non indexables</p>
              </div>
            </div>
            <div className="h-3 rounded-full overflow-hidden flex bg-[#E2E4ED]">
              <div className="bg-orion-primary h-full" style={{ width: `${summary.total > 0 ? (summary.indexablePages / summary.total) * 100 : 0}%` }} />
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-[#8B8FA7]">
              <span>Indexables ({summary.total > 0 ? Math.round((summary.indexablePages / summary.total) * 100) : 0}%)</span>
              <span>Non indexables ({summary.total > 0 ? Math.round((summary.nonIndexablePages / summary.total) * 100) : 0}%)</span>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-[#E2E4ED]">
              <div>
                <p className="text-xs text-[#8B8FA7] mb-1">Pages crawlees</p>
                <p className="text-2xl font-bold font-mono text-[#1B2559]">{summary.total}</p>
              </div>
              <div>
                <p className="text-xs text-[#8B8FA7] mb-1">Score global</p>
                <p className="text-2xl font-bold font-mono" style={{ color: scoreColor(score) }}>{score}%</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
